import { createCampaign, requestCampaignReview } from '@/api/campaignPlaner';
import type { CampaignResponse } from '@/api/campaignPlaner/fetchCampaign';
import { updateCampaign } from '@/api/campaignPlaner/updateCampaign';
import { updateCampaignName as updateCampaignNameApi } from '@/api/campaignPlaner/updateCampaignName';
import type { OptimisationInputs } from '@/dashboards/shared/optimisation/optimisationSession';
import { useCampaignOptimizationUrl } from '@/dashboards/CampaignOptimization/hooks/useCampaignOptimizationUrl';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { withOptimizerTimeout } from '@/optimizer/helpers/withOptimizerTimeout';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { dashboardRootRoute, toPath } from '@/utils/routes';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { createContext } from 'use-context-selector';

export type CampaignActionsContextValue = {
    campaign: CampaignResponse;
    campaignName: string;
    canSave: boolean;
    projectId: string;
    campaignId: string;
    saveCampaign: () => Promise<void>;
    saveCampaignAs: (name: string) => Promise<void>;
    updateCampaignName: (name: string) => Promise<void>;
    sendForReview: (comment: string) => Promise<void>;
    showSaveCampaignDialog: boolean;
    setShowSaveCampaignDialog: (value: boolean) => void;
    showSaveAsCampaignDialog: boolean;
    setShowSaveAsCampaignDialog: (value: boolean) => void;
    showRequestReviewConfirmationDialog: boolean;
    setShowRequestReviewConfirmationDialog: (value: boolean) => void;
};

export const CampaignActionsContext = createContext<CampaignActionsContextValue | null>(null);

const lockedRules = (inputs: OptimisationInputs) =>
    Object.entries(inputs.channels)
        .filter(([, channel]) => channel.locked && channel.investment != null)
        .map(([channel, value]) => ({
            channel,
            type: 'Total budget',
            subtype: 'is exactly',
            value: Number(value.investment),
        }));

export const CampaignActionsProvider = ({ campaign, children }: { campaign: CampaignResponse; children: ReactNode }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { params: { projectId } } = useCampaignOptimizationUrl();
    const campaignId = campaign.id;
    const { dashboard: { id: dashboardId } } = useDashboardGlobalContext();
    const { optimizerReady, optimizer } = useOptimizer();
    const { draft } = useOptimizationSession();

    const [showSaveCampaignDialog, setShowSaveCampaignDialog] = useState(false);
    const [showSaveAsCampaignDialog, setShowSaveAsCampaignDialog] = useState(false);
    const [showRequestReviewConfirmationDialog, setShowRequestReviewConfirmationDialog] = useState(false);

    const resolveActiveDaysPerMedium = useCallback(async (): Promise<Record<string, number> | undefined> => {
        if (!optimizerReady) return undefined;
        try {
            return await withOptimizerTimeout(optimizer.getActiveDaysPerMedium(), 'Active days per medium lookup');
        } catch (error) {
            // Optional metadata: a stale worker must not block saving. The timeout is already reported to Sentry.
            console.error('Failed to fetch active days per medium:', error);
            return undefined;
        }
    }, [optimizer, optimizerReady]);

    const invalidateCampaigns = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ['savedCampaigns'] });
        await queryClient.invalidateQueries({ queryKey: ['savedCampaign'] });
    }, [queryClient]);

    const saveCampaign = useCallback(async () => {
        if (!draft) return;
        const activeDaysPerMedium = await resolveActiveDaysPerMedium();
        await updateCampaign({
            projectId,
            campaignId,
            startDate: draft.startDate,
            endDate: draft.endDate,
            budget: Math.round(draft.budget),
            optimizationBase: draft.optimizationBase,
            dashboardId,
            rules: lockedRules(draft),
            activeDaysPerMedium,
        });
        await invalidateCampaigns();
        setShowSaveCampaignDialog(false);
    }, [draft, resolveActiveDaysPerMedium, projectId, campaignId, dashboardId, invalidateCampaigns]);

    const saveCampaignAs = useCallback(
        async (name: string) => {
            if (!draft) return;
            const activeDaysPerMedium = await resolveActiveDaysPerMedium();
            await createCampaign({
                projectId,
                name,
                startDate: draft.startDate,
                endDate: draft.endDate,
                budget: Math.round(draft.budget),
                optimizationBase: draft.optimizationBase,
                dashboardId,
                rules: lockedRules(draft),
                sourceCampaignId: campaign.id,
                activeDaysPerMedium,
            });
            await invalidateCampaigns();
            setShowSaveAsCampaignDialog(false);
            await navigate({ to: toPath(dashboardRootRoute, '/campaign-planner'), params: { projectId }, search: { dashboardId } });
        },
        [draft, resolveActiveDaysPerMedium, projectId, dashboardId, campaign.id, invalidateCampaigns, navigate],
    );

    const updateCampaignName = useCallback(
        async (name: string) => {
            await updateCampaignNameApi({ projectId, campaignId, name });
            await invalidateCampaigns();
        },
        [projectId, campaignId, invalidateCampaigns],
    );

    const sendForReview = useCallback(
        async (comment: string) => {
            await requestCampaignReview({ comment, campaignId: campaign.id, projectId });
            await invalidateCampaigns();
            setShowRequestReviewConfirmationDialog(false);
        },
        [campaign.id, projectId, invalidateCampaigns],
    );

    const value = useMemo<CampaignActionsContextValue>(
        () => ({
            campaign,
            campaignName: campaign.name ?? '',
            canSave: campaign.status === 'draft',
            projectId,
            campaignId,
            saveCampaign,
            saveCampaignAs,
            updateCampaignName,
            sendForReview,
            showSaveCampaignDialog,
            setShowSaveCampaignDialog,
            showSaveAsCampaignDialog,
            setShowSaveAsCampaignDialog,
            showRequestReviewConfirmationDialog,
            setShowRequestReviewConfirmationDialog,
        }),
        [campaign, projectId, campaignId, saveCampaign, saveCampaignAs, updateCampaignName, sendForReview, showSaveCampaignDialog, showSaveAsCampaignDialog, showRequestReviewConfirmationDialog],
    );

    return <CampaignActionsContext.Provider value={value}>{children}</CampaignActionsContext.Provider>;
};
